import { dt, perlin, time } from './index';
import {
  Entity,
  GameManager,
  GameObjectController,
  Particle,
  ParticleInfluencer,
  ParticleSystem,
  RenderItemType,
  Shape,
  SharedObjectType,
  V2,
} from './types';
import {
  addV2,
  clamp01,
  distanceBetweenSqr,
  isZeroV2,
  scaleV2,
  normalizeAndMagnitude,
  random,
  removeDenseArrayIndex,
  subtractV2,
  v2,
} from './helpers';
import { SHAPE_STYLES } from './constants';
import { addGameObject, getAbsolutePos } from './entity';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageCircle,
  type RenderPackageImage,
  type RenderPackageItem,
  type RenderPackageRect,
} from './render_package';
import logger from './controllers/logger';

export function controller(): GameObjectController {
  return {
    getNew: null,
    sync: null,
    update,
    createPackage,
  };
}

export function createAndAddParticleSystem(manager: GameManager, entity: Entity): ParticleSystem {
  let ps: ParticleSystem = {
    ...entity,
    paused: false,
    objectType: SharedObjectType.PARTICLE_SYSTEM,
    particles: [],
    influencers: [],
    spriteSheet: null,
  };

  ps.allowHighlight = false;
  ps.allowChildHighlight = false;

  addGameObject(manager, ps);

  return ps;
}

function update(ps: ParticleSystem) {
  if (ps.paused) return;

  updateParticles(ps.particles, ps.influencers, true, ps);
  for (let c = ps.influencers.length - 1; c >= 0; c--) {
    const inf = ps.influencers[c];
    if (inf.singleBurst || inf.parentEntity?.doKill) {
      removeDenseArrayIndex(ps.influencers, c);
    }
  }

  return false;
}

export function updateParticles(
  particles: Particle[],
  influencers: ParticleInfluencer[],
  endlessMove: boolean,
  ps: ParticleSystem,
) {
  for (let i = particles.length - 1; i >= 0; i--) {
    let p = particles[i];

    if (p.transparancyPerlin) {
      p.transparancyPerlin.time += dt * p.transparancyPerlin.speed;
      p.transparancyPerlin.value =
        p.transparancyPerlin.minValue +
        perlin.getVal(p.transparancyPerlin.time) * p.transparancyPerlin.valueRange;
      p.alpha = clamp01(p.transparancyPerlin.value);
    }

    if (p.sizePerlin) {
      p.sizePerlin.time += dt * p.sizePerlin.speed;
      p.sizePerlin.value =
        p.sizePerlin.minValue + perlin.getVal(p.sizePerlin.time) * p.sizePerlin.valueRange;
      p.size = p.sizePerlin.value;
    }

    if (p.maxLifeTime && p.fadeOutDuration) {
      p.isDisappearing = true;
      const fadeProgress = (p.maxLifeTime - p.lifeTime) / p.fadeOutDuration;
      const clampedProgress = 1 - clamp01(fadeProgress);

      p.fadeAlpha = 1 - clampedProgress;
    }

    p.lifeTime += dt;

    if (p.wanderSpeed) {
      p.angle = perlin.getVal(time * 0.1 + p.randomSeed * 100) * 2 * Math.PI;
      p.acc = addV2(
        p.acc,
        v2(Math.sin(p.angle) * p.wanderSpeed, Math.cos(p.angle) * p.wanderSpeed),
      );
    }

    if (p.gravity) {
      p.acc = addV2(p.acc, v2(0, p.gravity));
    }

    //drag
    if (!isZeroV2(p.vel)) {
      let { normalize, magnitude } = normalizeAndMagnitude(p.vel);
      let reverseDir = scaleV2(normalize, -1);
      let squareSpeed = magnitude * magnitude;

      let dragForce = scaleV2(reverseDir, squareSpeed * p.dragCoefficient);

      p.acc = addV2(p.acc, dragForce);
    }

    const influenceForce = getInfluencerForceAtPosition(addV2(p.pos), influencers);
    p.acc = addV2(p.acc, influenceForce);
    p.vel = addV2(p.vel, scaleV2(p.acc, dt));

    p.pos = addV2(p.pos, scaleV2(p.vel, dt));

    p.rot = p.rot + p.rotSpeed * dt;
    p.acc = v2();

    if (p.spriteAnimation) {
      if (p.animationTime > 0.5) {
        const nextIndex = p.spriteIndex + 1;
        p.spriteIndex = nextIndex % (p.spriteSheet.positionTable.length - 1);
        p.animationTime = 0;
      } else {
        p.animationTime += dt;
      }
    }

    if (p.maxLifeTime > 0 && p.lifeTime > p.maxLifeTime) {
      p.doKill = true;
    }

    if (p.doKill) {
      particles.splice(i, 1);
    }
  }
}

function getInfluencerForceAtPosition(pos: V2, influencers: ParticleInfluencer[]): V2 {
  let force: V2 = v2();

  for (let i = 0; i < influencers.length; i++) {
    const influencer = influencers[i];
    const influencerPos = influencer.parentEntity
      ? getAbsolutePos(influencer.parentEntity)
      : influencer.pos;
    const distSqr = distanceBetweenSqr(pos, influencerPos);
    const radiusSqr = influencer.radius * influencer.radius;
    if (distSqr >= radiusSqr) continue;

    const diffV2 = subtractV2(pos, influencerPos);
    if (!isZeroV2(diffV2)) {
      const { normalize, magnitude } = normalizeAndMagnitude(diffV2);
      const strength = 1 - magnitude / influencer.radius; //between 0-1
      const forceMag = influencer.innerStrength + strength * influencer.outerStrength;

      let addForce = scaleV2(normalize, forceMag);
      force = addV2(force, addForce);
    }
  }

  return force;
}

export function getZeroParticle(): Particle {
  const particle: Particle = {
    pos: v2(),
    vel: v2(),
    acc: v2(),
    shape: Shape.CIRCLE,
    rot: 0,
    rotSpeed: 0,
    size: 0,
    animations: [],
    shapeStyle: { ...SHAPE_STYLES.NONE },
    image: null,
    spriteSheet: null,
    spriteIndex: 0,
    spriteAnimation: false,
    animationTime: 0,
    lifeTime: 0,
    maxLifeTime: -1,
    wanderSpeed: 0,
    angle: 0,
    debug: false,
    randomSeed: random(0, 1),
    alpha: 0,
    fadeAlpha: 1,
    isDisappearing: false,
    transparancyPerlin: null,
    sizePerlin: null,
    wanderVel: {
      x: 0,
      y: 0,
    },
    dragCoefficient: 0,
    gravity: 0,
    fadeOutDuration: 0,
    doKill: false,
  };

  return particle;
}

export function fadeOutParticle(p: Particle, fadeDuration: number) {
  if (p.isDisappearing) return;
  p.fadeOutDuration = fadeDuration;
  p.maxLifeTime = p.lifeTime + fadeDuration;
}

export function createPackage(
  ps: ParticleSystem,
  position: V2,
  scale: number,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  for (let i = 0; i < ps.particles.length; i++) {
    createParticlePackage(ps.particles[i], position, scale, rPackage);
  }

  if (false) {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      width: 10,
      height: 10,

      shapeStyle: SHAPE_STYLES.DEBUG_ANCHOR,
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };

    rPackage.push(rect);

    let rect2: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x + ps.relativeMousePos.x * scale,
      positionY: position.y + ps.relativeMousePos.y * scale,
      width: 10,
      height: 10,

      shapeStyle: SHAPE_STYLES.DEBUG_ANCHOR,
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };

    rPackage.push(rect2);

    if (ps.influencers.length) {
      let rect2: RenderPackageRect = {
        type: RenderItemType.rect,
        ...EMPTY_RENDER_TRANSFORM,
        scale,
        positionX: position.x + scale * ps.influencers[0].pos.x,
        positionY: position.y + scale * ps.influencers[0].pos.y,
        width: 10,
        height: 10,

        shapeStyle: SHAPE_STYLES.DEBUG_GREEN,
        cornerRadius1: 0,
        cornerRadius2: 0,
        cornerRadius3: 0,
        cornerRadius4: 0,
        shadow: null,
      };

      rPackage.push(rect2);
    }
  }

  return rPackage;
}

export function createParticlePackage(
  p: Particle,
  position: V2,
  scale: number,
  rPackage: RenderPackageItem[] = [],
): RenderPackageItem[] {

  if (p.shape === Shape.CIRCLE) {
    let circle: RenderPackageCircle = {
      type: RenderItemType.circle,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x + scale * p.pos.x,
      positionY: position.y + scale * p.pos.y,
      radius: p.size / 2,
      // radius: 20,
      // shapeStyle: p.shapeStyle,
      shapeStyle: SHAPE_STYLES.DEBUG,
      shadow: null,
    };
    rPackage.push(circle);
  }

  if ((p.image && p.image.complete) || (p.spriteSheet && p.spriteSheet.complete)) {
    const size = p.shape === Shape.NONE ? p.size : p.size * 0.7;
    const halfSize = size / 2;

    let im: RenderPackageImage = {
      type: RenderItemType.image,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x + scale * (p.pos.x - halfSize),
      positionY: position.y + scale * (p.pos.y - halfSize),
      width: size,
      height: size,
      rotation: 0,
      opacity: p.alpha * p.fadeAlpha,
      flipHorizontally: false,
      shape: Shape.RECT,
      radius: 0,
      imageId: '',
    };

    if (p.spriteSheet) {
      im.imageId = p.spriteSheet.imageId;
      im.partial = true;
      im.sourcePos = p.spriteSheet.positionTable[p.spriteIndex];
      im.sourceWidth = p.spriteSheet.spriteWidth;
      im.sourceHeight = p.spriteSheet.spriteHeight;
    }

    rPackage.push(im);
  }

  return rPackage;
}
